import {
  ListStoriesRequest,
  GetStoryRequest,
  ListStoriesByMonthRequest,
  ListMonthlyThemesRequest,
  GetHomeRequest,
  ListCommentsRequest,
  ListCommentsResponse,
  CreateCommentRequest,
  Story,
  Comment,
  MonthlyTheme,
  HomeContent,
} from "../generated/stories/v1/story_pb";
import { StoryServiceClient } from "../generated/stories/v1/StoryServiceClientPb";
import { getGrpcEndpoint } from "./grpcEndpoint";

const GRPC_ENDPOINT = getGrpcEndpoint();

// Create client instance
const client = new StoryServiceClient(GRPC_ENDPOINT);

/**
 * Public Stories Service Client
 * Provides methods to fetch stories and related content from the service
 */
class PublicStoriesService {
  /**
   * List stories with optional filters
   */
  async listStories(options?: {
    keyword?: string;
    monthId?: string;
    tags?: string[];
    page?: number;
    pageSize?: number;
  }): Promise<{
    stories: Story.AsObject[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    return new Promise((resolve, reject) => {
      const request = new ListStoriesRequest();
      
      if (options?.keyword) {
        request.setKeyword(options.keyword);
      }
      if (options?.monthId) {
        request.setMonthId(options.monthId);
      }
      if (options?.tags && options.tags.length > 0) {
        request.setTagsList(options.tags);
      }
      if (options?.page) {
        request.setPage(options.page);
      }
      if (options?.pageSize) {
        request.setPageSize(options.pageSize);
      }

      client.listStories(request, null, (err, response) => {
        if (err) {
          console.error("Error fetching stories:", err);
          reject(err);
          return;
        }

        const stories = response.getStoriesList().map((story) => story.toObject());
        resolve({
          stories,
          total: response.getTotal(),
          page: response.getPage(),
          pageSize: response.getPageSize(),
          totalPages: response.getTotalPages(),
        });
      });
    });
  }

  /**
   * Get a single story by ID or slug
   */
  async getStory(identifier: { id?: string; slug?: string }): Promise<Story.AsObject> {
    return new Promise((resolve, reject) => {
      const request = new GetStoryRequest();
      
      if (identifier.id) {
        request.setId(identifier.id);
      } else if (identifier.slug) {
        request.setSlug(identifier.slug);
      } else {
        reject(new Error("Either id or slug must be provided"));
        return;
      }

      client.getStory(request, null, (err, response) => {
        if (err) {
          console.error("Error fetching story:", err);
          reject(err);
          return;
        }

        resolve(response.toObject());
      });
    });
  }

  /**
   * List stories by month
   */
  async listStoriesByMonth(monthId: string, options?: {
    page?: number;
    pageSize?: number;
  }): Promise<{
    theme: MonthlyTheme.AsObject;
    stories: Story.AsObject[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    return new Promise((resolve, reject) => {
      const request = new ListStoriesByMonthRequest();
      request.setMonthId(monthId);
      
      if (options?.page) {
        request.setPage(options.page);
      }
      if (options?.pageSize) {
        request.setPageSize(options.pageSize);
      }

      client.listStoriesByMonth(request, null, (err, response) => {
        if (err) {
          console.error("Error fetching stories by month:", err);
          reject(err);
          return;
        }

        const stories = response.getStoriesList().map((story) => story.toObject());
        resolve({
          theme: response.getTheme()?.toObject() || {} as MonthlyTheme.AsObject,
          stories,
          total: response.getTotal(),
          page: response.getPage(),
          pageSize: response.getPageSize(),
          totalPages: response.getTotalPages(),
        });
      });
    });
  }

  /**
   * List all monthly themes
   */
  async listMonthlyThemes(): Promise<MonthlyTheme.AsObject[]> {
    return new Promise((resolve, reject) => {
      const request = new ListMonthlyThemesRequest();

      client.listMonthlyThemes(request, null, (err, response) => {
        if (err) {
          console.error("Error fetching monthly themes:", err);
          reject(err);
          return;
        }

        const themes = response.getThemesList().map((theme) => theme.toObject());
        resolve(themes);
      });
    });
  }

  /**
   * Get homepage content (upcoming events + monthly themed sections)
   */
  async getHome(options?: {
    eventsLimit?: number;
    monthsLimit?: number;
    storiesPerMonth?: number;
  }): Promise<HomeContent.AsObject> {
    return new Promise((resolve, reject) => {
      const request = new GetHomeRequest();
      
      if (options?.eventsLimit) {
        request.setEventsLimit(options.eventsLimit);
      }
      if (options?.monthsLimit) {
        request.setMonthsLimit(options.monthsLimit);
      }
      if (options?.storiesPerMonth) {
        request.setStoriesPerMonth(options.storiesPerMonth);
      }

      client.getHome(request, null, (err, response) => {
        if (err) {
          console.error("Error fetching home content:", err);
          reject(err);
          return;
        }

        resolve(response.getContent()?.toObject() || {} as HomeContent.AsObject);
      });
    });
  }

  /**
   * List comments for a story (guests: hidden comments excluded).
   */
  async listComments(storyId: string): Promise<{ commentsList: Comment.AsObject[] }> {
    return new Promise((resolve, reject) => {
      const request = new ListCommentsRequest();
      request.setStoryId(storyId);
      request.setIncludeHidden(false);

      client.listComments(request, null, (err, response: ListCommentsResponse) => {
        if (err) {
          console.error("Error fetching comments:", err);
          reject(err);
          return;
        }
        resolve({
          commentsList: response.getCommentsList().map((c) => c.toObject()),
        });
      });
    });
  }

  /**
   * Create a comment (guest). Requires storyId, authorName, body; authorEmail optional.
   */
  async createComment(params: {
    storyId: string;
    authorName: string;
    authorEmail?: string;
    body: string;
  }): Promise<Comment.AsObject> {
    return new Promise((resolve, reject) => {
      const request = new CreateCommentRequest();
      request.setStoryId(params.storyId);
      request.setAuthorName(params.authorName);
      if (params.authorEmail) request.setAuthorEmail(params.authorEmail);
      request.setBody(params.body);

      client.createComment(request, null, (err, response) => {
        if (err) {
          console.error("Error creating comment:", err);
          reject(err);
          return;
        }
        resolve(response.toObject());
      });
    });
  }
}

// Export singleton instance
export const storiesService = new PublicStoriesService();
